import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

/**
 * Batch 2 / Round 7 — New Lot Number modal.
 *
 * Legacy parity: frmLotNum.vb — a single integer prompt opened when the
 * inspector picks "New Lot #" in the lot dropdown. Validation messages
 * are legacy-verbatim:
 *   • non-integer input → "Please enter a valid number"
 *   • duplicate of an existing lot (checked by the PARENT, which owns
 *     the lot list) → "Lot number N already exists. Please double check
 *     your entry and try again." (MDIParent1.cboJobLot_SelectedIndexChanged)
 *
 * Props:
 *   open                — show/hide
 *   existingLots        — string[] of current lot numbers (for the
 *                         duplicate message; mirrors cboJobLot.Items)
 *   onCancel()          — DialogResult.Cancel
 *   onConfirm(lotNum)   — DialogResult.OK with the integer lot number
 */
const NewLotModal = ({ open, existingLots = [], onCancel, onConfirm }) => {
  const [value, setValue] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setValue("");
      setErr("");
    }
  }, [open]);

  const handleOk = useCallback(() => {
    const trimmed = (value ?? "").toString().trim();
    const n = Number(trimmed);

    // Legacy frmLotNum: CInt(TextBox1.Text) — integer or bust.
    if (trimmed === "" || !Number.isInteger(n)) {
      setErr("Please enter a valid number");
      return;
    }

    // Legacy MDIParent1: duplicate lot rejected before being added.
    if (existingLots.map((l) => String(l).trim()).includes(String(n))) {
      setErr(
        `Lot number ${n} already exists. Please double check your entry and try again.`
      );
      return;
    }

    setErr("");
    onConfirm?.(n);
  }, [value, existingLots, onConfirm]);

  return (
    <Modal isOpen={!!open} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        <i className="ri-archive-line align-bottom me-2 text-primary"></i>
        New Lot Number
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleOk();
          }}
        >
          <FormGroup className="mb-2">
            <Label for="new-lot-num">Lot Number</Label>
            <Input
              id="new-lot-num"
              type="number"
              inputMode="numeric"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter new lot number"
              autoFocus
            />
          </FormGroup>
          {err ? (
            <div className="alert alert-warning py-2 px-3 small mb-0" role="alert">
              {err}
            </div>
          ) : null}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleOk}>
          <i className="ri-check-line align-bottom me-1"></i>
          OK
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(NewLotModal);
